import { Async, type AsyncState } from '../../platform/async/Async'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { Skeleton } from '../../platform/async/Skeleton'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { InstitutionAvatar } from '../../platform/primitives/InstitutionAvatar'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { currenciesOf, PerCurrencySubtotals } from './AccountsSummary'
import { formatRelative, institutionsStaleLabel } from './accountsStaleLabels'
import { balancesForTotals, totalsOf } from './accountMoney'
import { emptyUnlessGroups, healthFor, linkedGroupsOf, relinkIdOf, type InstitutionGroup } from './connectionGroups'
import { ReconnectAction } from './ReconnectAction'
import { activeRelinkFor, MatchAccountsLink, RelinkNotices } from './RelinkNotices'
import { StatusPill } from './connectionStatus'
import type { Account } from './useAccounts'
import { useProviderItems, type ProviderItem } from './useAggregator'
import './accounts.css'

/**
 * The Institutions panel — M5 Ticket 2 (#425 §A3). A summary, inline on
 * `/accounts`; `Connections.tsx` (mounted at `/connections`) stays the
 * management screen, unchanged in role. **`/accounts/connections` names no
 * route at all** — it was `PRD-M5-accounts.md`'s own invention (M5-R32),
 * copied into the plan and never built; `App.tsx` mounts `Connections` at
 * `/connections` only. Every link from this component goes there directly.
 */

/** `[...s][0]`, not `s.charAt(0)` — same Unicode-safety discipline
 *  `MerchantAvatar`'s initial and `PersonAvatar`'s `initialsFor` already
 *  apply, so a provider name is capitalised without risking a split
 *  surrogate pair. No `'plaid'`/`'flinks'` literal here or anywhere in this
 *  file — `registry.test.ts`'s guard reserves those two quoted strings for
 *  `providers/registry.ts` alone; this reads whatever the wire's own
 *  `ProviderType` string is and capitalises it generically. */
function capitalize(word: string): string {
  const first = [...word][0]
  return first === undefined ? word : first.toUpperCase() + word.slice(first.length)
}

function sourceOf(group: InstitutionGroup): string {
  if (group.kind === 'manual') return 'You update this yourself'
  if (healthFor(group)?.kind === 'disconnected') return 'Disconnected — history kept'
  // No item yet (the accounts query is ahead of the items query): name the
  // provider off the account and claim nothing about sync recency.
  // An item-less group exists only because an account carries it, so
  // `accounts[0]` is always there, but `provider` is typed nullable on the
  // wire. The empty string is that residue: it claims nothing, rather than
  // inventing a "Linked" provider name.
  if (group.item === null) {
    const provider = group.accounts[0]?.provider
    return provider ? capitalize(provider) : ''
  }
  const label = capitalize(group.item.provider)
  return group.item.lastSyncedAt
    ? `${label} · synced ${formatRelative(group.item.lastSyncedAt)}`
    : `${label} · not yet synced`
}

function InstitutionRow({ group, items }: { group: InstitutionGroup; items: ProviderItem[] }) {
  const totals = totalsOf(group.accounts)
  const currencies = currenciesOf(group.accounts)
  // `connectionGroups.ts`'s shared `healthFor`, not a second derivation of
  // this row's own (S4, final review). `healthFor` reads `connection_status`
  // and never an item's absence, so an item-less group can still read `reauth`
  // off a race (accounts ahead of items). Its `Reconnect →` link goes to
  // `/connections`, whose card has no Reconnect action without an item, so the
  // link is gated on `group.item` below rather than on health alone.
  const health = healthFor(group)
  const accountLine = group.accounts.map((a) => a.name).join(' · ')
  // A freshly linked item can have accounts whose balances have not arrived
  // yet — `totalsOf([])` and `totalsOf([{currentCents: null, …}])` both
  // return a non-null `Totals` with every figure at 0 (`accountMoney.ts`'s
  // own `withBalance` filter empties out, so the mixed-currency guard never
  // fires). Rendering that as `$0.00` would be a fabricated figure on a row
  // that already says "No accounts yet" or lists an account with nothing to
  // report — the plan's own Deferred preamble: absent, not a zero.
  const hasBalance = balancesForTotals(group.accounts).length > 0
  // Only a `reauth` row WITH an item carries a real action (`Reconnect →`) —
  // the hover fill is scoped to match, so a row with nothing to click does not
  // look clickable (architect review, #425 T2 gate, non-blocking).
  const repairable = health?.kind === 'reauth' && group.item !== null
  // A disconnected bank the server says can be reconnected carries the SAME
  // action as its card on `/connections` (`ReconnectAction`) — its own button,
  // not a link: a reconnect starts here, a repair is managed there.
  const reconnectable = relinkIdOf(group) !== null
  // A reconnect already under way for this (disconnected) bank: its action is to
  // finish matching, not to start another.
  const oldItemId = group.accounts[0]?.itemId ?? null
  const active =
    health?.kind === 'disconnected' && oldItemId !== null ? activeRelinkFor(items, oldItemId) : null
  const actionable = repairable || reconnectable || active !== null
  // Phone: the row wraps so the action takes its own line instead of crushing
  // the name into a one-character column. `useBreakpoint()` is the only
  // breakpoint source (rule 5), so this is a class, never a media query.
  const phone = useBreakpoint() === 'phone'
  const rowClassName = [
    'institutions-panel__row',
    actionable && 'institutions-panel__row--actionable',
    phone && 'institutions-panel__row--wrap',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rowClassName} role="listitem" aria-label={group.name}>
      <InstitutionAvatar name={group.name} logoUrl={group.logoUrl} />
      <span className="institutions-panel__identity">
        <span className="institutions-panel__name">{group.name}</span>
        <span className="institutions-panel__meta">{accountLine || 'No accounts yet'}</span>
        <span className="institutions-panel__meta">{sourceOf(group)}</span>
      </span>
      <span className="institutions-panel__total">
        {hasBalance &&
          (totals ? (
            <Money cents={totals.netWorthCents} currency={totals.currency ?? undefined} kind="stock" />
          ) : (
            <PerCurrencySubtotals
              accounts={group.accounts}
              currencies={currencies}
              pick={(t) => t.netWorthCents}
            />
          ))}
      </span>
      <StatusPill health={health} />
      {repairable && (
        <InlineLink to="/connections" className="institutions-panel__action">
          Reconnect →
        </InlineLink>
      )}
      {active !== null && (
        <span className="institutions-panel__action-slot">
          <MatchAccountsLink newItemId={active.id} />
        </span>
      )}
      {reconnectable && active === null && (
        <span className="institutions-panel__action-slot">
          <ReconnectAction group={group} entryPoint="accounts_panel" />
        </span>
      )}
    </div>
  )
}

const SKELETON = (
  <>
    <Skeleton height={52} />
    <Skeleton height={52} />
  </>
)

export function InstitutionsPanel({ accounts }: { accounts: Account[] }) {
  const state = useProviderItems()
  const manualAccounts = accounts.filter((a) => a.provider === 'manual')
  // Always built from `accounts` alone, never from the items query — a
  // manual-only household has ZERO items, and `useProviderItems()`'s own
  // `isEmpty` (`items.length === 0`) would otherwise hide this row behind
  // the nested `<Async>`'s empty state, contradicting "every institution
  // including Manual" (#425 §A3's own acceptance line). Structurally, an
  // empty `items` list alongside a non-empty `accounts` list can only mean
  // every LINKED account is missing its item too (the rare `orphan` case) —
  // manual accounts never depend on this query at all.
  const manualGroup: InstitutionGroup | null =
    manualAccounts.length > 0
      ? { key: 'manual', kind: 'manual', item: null, name: 'Manual', logoUrl: null, accounts: manualAccounts }
      : null

  // Reframes `empty` into a genuinely empty SUCCESS (`data: []`) whenever
  // there is still something to show — a Manual row, or an orphan group
  // (`emptyUnlessGroups`, `connectionGroups.ts`, the same predicate the
  // Connections tile uses) — the panel is not empty while a row is visible, so `<Async>`'s own empty title/body
  // must not render alongside it (architect review, #425 T2 gate, blocking).
  // **This DOES change what renders**, and an earlier version of this
  // comment claimed the opposite: with `manualGroup` set, `<Async>` now
  // calls `children` where it previously rendered the empty message
  // instead — which is exactly how the orphan-account row
  // (`InstitutionsPanel.test.tsx`'s own "non-manual account with a null
  // itemId" case) reaches the screen at all when `items` is `[]`. The
  // narrower, TRUE claim: it is bounded to one state (`empty`) and one
  // condition (a Manual row or an orphan group exists), and `stale` can never be reached from
  // it — `fromQuery.ts` returns `empty` before `asOf` is ever consulted, and
  // `useProviderItems()`'s own `asOf` returns `undefined` for an empty list
  // regardless — so this cannot manufacture #429's byte-identical-banner
  // failure. Only a household with neither a Manual row nor an orphan group
  // reaches the real `empty` state; orphans render as rows.
  //
  // The real, ongoing cost (architect review, #425 T2 re-gate, non-blocking,
  // filed as #431 — not built here): this surface can never report `empty`
  // to anything downstream that later grows to care about that fact — an
  // `empty_state_shown` analytics event, or a future `<Async empty={{
  // action }}>` on this same call site — because a household with a Manual
  // row or an orphan group always masks it as `success`. Not a defect in what ships today, since nothing
  // downstream reads it yet; a reason not to reach for `<Async>`'s API here
  // if that changes.
  const effectiveState: AsyncState<ProviderItem[]> = emptyUnlessGroups(
    state,
    accounts,
    manualGroup !== null,
  )

  return (
    <Panel className="institutions-panel">
      <div className="institutions-panel__header">
        <span>
          <h3 className="institutions-panel__title">Institutions</h3>
          <p className="institutions-panel__sub">Where the data comes from, and how fresh it is</p>
        </span>
        {/* `InlineLink`, not a filled `Button` — a standalone text link
            (rule 11), and non-fill text does not count against rule 9's
            one-`--accent`-fill budget (the Net worth hero already spends
            it). `/connections`, not `/accounts/connections` — see this
            file's own docblock. */}
        <InlineLink to="/connections" className="institutions-panel__manage">
          Manage connections →
        </InlineLink>
      </div>
      {/*
       * `<Async>` owns every one of its five states exactly once, and
       * `role="list"` never wraps it — every other list in this module puts
       * `role="list"` inside the render prop, and a list may own only
       * `listitem` children. `<Async>`'s own skeleton/empty/error markup
       * (a `role="status"` live region on EVERY state, per its own source,
       * plus an `aria-busy` div, an `AlertPanel`/`role="alert"`, or the
       * empty panel on the other four) is real markup, not a `listitem` —
       * a `role="presentation"` wrapper does NOT fix this, because ARIA
       * ownership follows descent, not directness: `role="status"` inside a
       * presentational wrapper is still a non-`listitem` DESCENDANT of the
       * list (architect re-gate, #425 T2 gate, blocking — the wrapper this
       * replaced was inert and its docblock's `<table role="presentation">`
       * citation was the OPPOSITE mechanism, downward inheritance, not
       * upward transparency).
       *
       * The Manual row still has to render, and be found via `role="list",
       * name: 'Institutions'`, on every state that is not success/stale —
       * `<Async>`'s OWN loading/empty/error output renders where `<Async>`
       * sits, and Manual has no async dependency of its own to wait on. So
       * two mutually exclusive branches, keyed on `effectiveState` (not the
       * raw query `state` — with `manualGroup` set, `effectiveState` can
       * already read `success` while `state` still reads `empty`, and using
       * the raw value here would render BOTH branches at once, two
       * `role="list"` elements answering to the same name): `<Async>`'s own
       * render prop supplies the list on success/stale, and this component
       * supplies an equivalent list on every other state — never both.
       *
       * **The list can be ABSENT entirely** — a manual-less household on
       * loading/error/empty has nothing to list yet, so neither branch
       * renders one (architect review, #425 T2 re-gate, non-blocking).
       * Correct, since there is nothing to announce as a list, but it means
       * `role="list", name: 'Institutions'` is not a fixture of this panel
       * the way it would be on a page whose list always exists — worth
       * knowing before writing a spec that expects to find it unconditionally.
       */}
      <Async
        surface="aggregator-items"
        state={effectiveState}
        skeleton={SKELETON}
        empty={{ title: 'No connections yet', body: 'Link a bank to see it here.' }}
        staleLabel={institutionsStaleLabel}
      >
        {(items) => (
          <>
            <RelinkNotices items={items} nested />
            <div role="list" aria-label="Institutions">
              {linkedGroupsOf(accounts, items).map((group) => (
                <InstitutionRow group={group} items={items} key={group.key} />
              ))}
              {manualGroup && <InstitutionRow group={manualGroup} items={items} key={manualGroup.key} />}
            </div>
          </>
        )}
      </Async>
      {effectiveState.status !== 'success' && effectiveState.status !== 'stale' && manualGroup && (
        <div role="list" aria-label="Institutions">
          <InstitutionRow group={manualGroup} items={[]} key={manualGroup.key} />
        </div>
      )}
    </Panel>
  )
}
